<template>
  <div :class="colClass">
    <div
      class="ranking-stat-box"
      :class="getClass"
      v-if="pillarData && pillarData.rank"
    >
      <div class="rank-value container d-flex h-100">
        <div class="align-self-center" style="width: 100%">
          <span
            class="field-value"
            :class="{ 'first-row-value': isFirstRow }"
            >{{ pillarData.rank }}</span
          ><br />
          <span :class="getFieldNameClass">{{ scoreField }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CityRanking",
  props: {
    scoreField: {
      type: String,
    },
    pillarData: {
      type: Object,
    },
    pillarType: {
      type: String,
    },
    isSubPillar: {
      type: Boolean,
      default: false,
    },
    isFirstRow: {
      type: Boolean,
      default: false,
    },
    topThree: {
      type: Array,
      default: () => [],
    },
    colSize: {
      type: Number,
      default: 3,
    },
  },
  computed: {
    colClass() {
      // Use col-1 for 8 columns (12/8 ≈ 1.5, so we'll use custom sizing)
      return this.colSize === 1 ? "col-custom-8" : `col-${this.colSize}`;
    },
    getFieldNameClass() {
      if (this.isFirstRow) {
        return "field-name-first-row";
      }
      return this.isSubPillar ? "field-name" : "field-name-pillar";
    },
    getClass() {
      if (!this.pillarData || !this.pillarData.rank) return "";

      // For first row, use grey background with colored text
      if (this.isFirstRow) {
        return `${this.pillarType} first-row-item`;
      }

      const rank = this.pillarData.rank;
      let colorClass = "";

      // Check if this is a sub-pillar and if it's in the top 3 performing areas
      if (this.isSubPillar && this.topThree && this.topThree.length > 0) {
        const pillarKey = this.scoreField
          .toLowerCase()
          .replace(/[^a-z]/g, "_")
          .replace(/__+/g, "_")
          .replace(/^_|_$/g, "");
        const isTopPerformer = this.topThree.some(([key]) => {
          const normalizedKey = key
            .toLowerCase()
            .replace(/[^a-z]/g, "_")
            .replace(/__+/g, "_")
            .replace(/^_|_$/g, "");
          const normalizedPillarKey = pillarKey
            .replace(/[^a-z]/g, "_")
            .replace(/__+/g, "_")
            .replace(/^_|_$/g, "");
          return (
            normalizedKey === normalizedPillarKey ||
            normalizedKey.includes(normalizedPillarKey) ||
            normalizedPillarKey.includes(normalizedKey)
          );
        });

        if (isTopPerformer) {
          colorClass = "green"; // Top performing area
        } else if (rank <= 51) {
          // Middle half
          colorClass = "yellow"; // Room for improvement
        } else {
          colorClass = "red"; // Requires attention
        }
      } else {
        // For main pillars, use standard ranking logic
        if (rank <= 34) {
          colorClass = "green"; // Top third
        } else if (rank <= 68) {
          colorClass = "yellow"; // Middle third
        } else {
          colorClass = "red"; // Bottom third
        }
      }

      return `${this.pillarType} ${colorClass}`;
    },
  },
};
</script>

<style scoped>
/* Custom column sizing for 8 equal columns */
.col-custom-8 {
  flex: 0 0 12.5%;
  max-width: 12.5%;
  position: relative;
  width: 100%;
  padding-right: 0;
  padding-left: 0;
}

.ranking-stat-box {
  display: flex;
  justify-content: center;
  align-content: center;
  flex-direction: column;
  text-align: center;
  margin: 0;
  min-height: 115px;
  color: white;
  border: 0.5px solid #ffffff !important;
}

.field-value {
  font-size: large;
  font-weight: 300;
  font-size: 20px;
}

.field-name {
  font-size: 14px;
  font-weight: 800;
}

.field-name-pillar {
  font-size: 14px;
  font-weight: 800;
}

.first-row-value {
  font-size: 28px !important;
  font-weight: 700 !important;
}

.field-name-first-row {
  font-size: 16px !important;
  font-weight: 900 !important;
}

/* First row styling with grey background and colored text */
.first-row-item {
  background: #f5f5f5 !important;
  color: #333 !important;
}

.people_centricity.first-row-item .field-value,
.people_centricity.first-row-item .field-name-first-row {
  color: #e74c3c !important;
}

.openness.first-row-item .field-value,
.openness.first-row-item .field-name-first-row {
  color: #e67e22 !important;
}

.sustainability.first-row-item .field-value,
.sustainability.first-row-item .field-name-first-row {
  color: #27ae60 !important;
}

.institutions_governance.first-row-item .field-value,
.institutions_governance.first-row-item .field-name-first-row {
  color: #8e44ad !important;
}

.transport_infrastructure.first-row-item .field-value,
.transport_infrastructure.first-row-item .field-name-first-row {
  color: #3498db !important;
}

.innovation.first-row-item .field-value,
.innovation.first-row-item .field-name-first-row {
  color: #e74c3c !important;
}

.vibrancy.first-row-item .field-value,
.vibrancy.first-row-item .field-name-first-row {
  color: #f39c12 !important;
}

.e_intelligence.first-row-item .field-value,
.e_intelligence.first-row-item .field-name-first-row {
  color: #16a085 !important;
}

/* Traffic Light Colors - Applied to All Pillars */
/* Red = Poor Performance (Bottom third/Low ranks) - Lightest */
.red {
  background: #e89b85 !important; /* Light Red */
}

/* Yellow/Orange = Medium Performance (Middle third) - Medium opacity */
.yellow {
  background: rgba(255, 150, 63, 0.7) !important; /* Medium Orange */
}

/* Green = Good Performance (Top third/High ranks) - Full opacity */
.green {
  background: rgba(40, 167, 69, 1) !important; /* Full Green */
}
</style>
